import { Box, CircularProgress, Typography } from '@mui/material';
import React from 'react';
import { VeraPromptProposal } from '../../../models/admin';
import VeraPromptContextGuide from '../VeraPromptContextGuide';
import { getVeraPromptFullPipelineGuide } from '../veraPromptContextGuides';
import { StyledButton } from '../../styles/StyledButtons';
import { StyledTypography } from '../../styles/styles';
import { formatChangedPromptLabel } from './veraGlobalChatPromptOverrideUtils';

interface InvestorProposalFooterProps {
  saving: boolean;
  error: string | null;
  submittedProposal: VeraPromptProposal | null;
  markdown: string;
  isDirty: boolean;
  intentIsDirty: boolean;
  groundingIsDirty: boolean;
  promptSynced: boolean;
  intentSynced: boolean;
  groundingSynced: boolean;
  showGroundingPromptTab: boolean;
  globalGuideOptions: { googleSearchEnabled: boolean; googleSearchLinksEnabled: boolean };
  onSubmitProposal: () => void;
}

export default function InvestorProposalFooter({
  saving,
  error,
  submittedProposal,
  markdown,
  isDirty,
  intentIsDirty,
  groundingIsDirty,
  promptSynced,
  intentSynced,
  groundingSynced,
  showGroundingPromptTab,
  globalGuideOptions,
  onSubmitProposal,
}: InvestorProposalFooterProps) {
  const hasUnsubmittedChanges = isDirty || intentIsDirty || groundingIsDirty;

  return (
    <>
      {error && (
        <StyledTypography tone="tone1" sx={{ color: 'error.main', mb: 2 }}>
          {error}
        </StyledTypography>
      )}
      {submittedProposal?.externalId && (
        <StyledTypography tone="tone1" sx={{ color: 'success.main', mb: 2 }}>
          ✓ Proposal {submittedProposal.externalId} submitted for engineering review.
          {(submittedProposal.changedPrompts ?? []).length > 0 && (
            <>
              {' '}
              — includes changes to{' '}
              {submittedProposal.changedPrompts!.map((promptKey, index) => (
                <React.Fragment key={promptKey}>
                  {index > 0 ? ' and ' : ''}
                  <strong>{formatChangedPromptLabel(promptKey)}</strong>
                </React.Fragment>
              ))}
            </>
          )}
        </StyledTypography>
      )}

      <VeraPromptContextGuide
        title="Full investor chat pipeline — copy for AI Studio"
        markdown={getVeraPromptFullPipelineGuide('global', globalGuideOptions)}
      />

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <StyledButton onClick={onSubmitProposal} disabled={saving || !markdown.trim()}>
          {saving ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
          Submit Proposal
        </StyledButton>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography
            variant="caption"
            sx={{ color: promptSynced ? 'success.main' : 'text.secondary', fontStyle: 'italic' }}
          >
            {promptSynced ? '✓ System prompt saved' : 'Saving system prompt…'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            ·
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: intentSynced ? 'success.main' : 'text.secondary', fontStyle: 'italic' }}
          >
            {intentSynced ? '✓ Intent prompt saved' : 'Saving intent prompt…'}
          </Typography>
          {showGroundingPromptTab && (
            <>
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                ·
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: groundingSynced ? 'success.main' : 'text.secondary',
                  fontStyle: 'italic',
                }}
              >
                {groundingSynced ? '✓ Google Search prompt saved' : 'Saving Google Search prompt…'}
              </Typography>
            </>
          )}
        </Box>
        {hasUnsubmittedChanges && (
          <Typography variant="caption" sx={{ color: 'warning.main', fontStyle: 'italic' }}>
            Unsubmitted changes
          </Typography>
        )}
      </Box>
    </>
  );
}
